import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Lock, Check } from 'lucide-react'
import api from '../services/api'

type Step = 'email' | 'otp' | 'password'

export default function ForgotPassword() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSendOTP = async () => {
    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const result = await api.sendPasswordResetOTP(email)

      if (result.success) {
        setCurrentStep('otp')
      } else {
        setError(result.message || 'Failed to send OTP. Please try again.')
      }
    } catch (err) {
      console.error('Send OTP error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP code')
      return
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const result = await api.verifyPasswordResetOTP(email, otp)

      if (result.success) {
        setCurrentStep('password')
      } else {
        setError(result.message || 'Invalid OTP. Please try again.')
      }
    } catch (err) {
      console.error('Verify OTP error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setError('Please enter a new password')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const result = await api.resetPassword(email, otp, newPassword)

      if (result.success) {
        alert('Password reset successfully! Please log in with your new password.')
        navigate('/login')
      } else {
        setError(result.message || 'Failed to reset password. Please try again.')
      }
    } catch (err) {
      console.error('Reset password error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setError('')
    setIsLoading(true)

    try {
      const result = await api.sendPasswordResetOTP(email)

      if (result.success) {
        alert('OTP sent successfully! Check your email.')
      } else {
        setError(result.message || 'Failed to resend OTP.')
      }
    } catch (err) {
      console.error('Resend OTP error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        currentStep === 'email' ? 'bg-onrecord-orange text-white' : 'bg-green-500 text-white'
      }`}>
        {currentStep === 'email' ? '1' : <Check className="w-5 h-5" />}
      </div>
      <div className={`w-16 h-1 ${
        currentStep === 'email' ? 'bg-gray-300' : 'bg-green-500'
      }`} />
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        currentStep === 'email' ? 'bg-gray-300 text-gray-600' :
        currentStep === 'otp' ? 'bg-onrecord-orange text-white' : 'bg-green-500 text-white'
      }`}>
        {currentStep === 'password' ? <Check className="w-5 h-5" /> : '2'}
      </div>
      <div className={`w-16 h-1 ${
        currentStep === 'password' ? 'bg-green-500' : 'bg-gray-300'
      }`} />
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        currentStep === 'password' ? 'bg-onrecord-orange text-white' : 'bg-gray-300 text-gray-600'
      }`}>
        3
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/login')}
            className="text-gray-700 hover:text-onrecord-orange rounded-full p-2 hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {renderStepIndicator()}

          {/* Step 1: Email */}
          {currentStep === 'email' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-onrecord-orange" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Enter Your Email</h2>
                <p className="text-gray-600 text-sm">
                  We'll send you a verification code to reset your password
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendOTP()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange"
                  placeholder="your@email.com"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleSendOTP}
                disabled={isLoading}
                className="w-full py-3 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send OTP'}
              </button>
            </div>
          )}

          {/* Step 2: OTP */}
          {currentStep === 'otp' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-onrecord-orange" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Enter OTP Code</h2>
                <p className="text-gray-600 text-sm">
                  We sent a 6-digit code to<br />
                  <span className="font-medium text-gray-900">{email}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyPress={(e) => e.key === 'Enter' && handleVerifyOTP()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-onrecord-orange"
                  placeholder="000000"
                  maxLength={6}
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleVerifyOTP}
                disabled={isLoading}
                className="w-full py-3 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="text-center">
                <button
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-sm text-onrecord-orange hover:text-onrecord-orange-dark font-medium disabled:opacity-50"
                >
                  Didn't receive code? Resend
                </button>
              </div>
            </div>
          )}

          {/* Step 3: New Password */}
          {currentStep === 'password' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-onrecord-orange" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Create New Password</h2>
                <p className="text-gray-600 text-sm">
                  Please enter a strong password (min 8 characters)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange"
                  placeholder="Enter new password"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleResetPassword()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange"
                  placeholder="Confirm new password"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleResetPassword}
                disabled={isLoading}
                className="w-full py-3 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          )}
        </div>

        {/* Back to Login */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/login')}
            className="text-gray-600 hover:text-onrecord-orange text-sm font-medium"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  )
}
